/**
 * Lists — checklists for shopping, packing, a job's steps.
 *
 * Restored from the v2 app, which the first migration pass missed entirely.
 * Deliberately touch-first: rows are large enough to hit with a thumb, and
 * ticking an item is the whole interaction.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Plus, Trash2, ListChecks, ExternalLink, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export default function ListsPage() {
  const lists = useQuery(api.lists.list, {});
  const createList = useMutation(api.lists.create);
  const removeList = useMutation(api.lists.remove);
  const addItem = useMutation(api.lists.addItem);
  const updateItem = useMutation(api.lists.updateItem);
  const removeItem = useMutation(api.lists.removeItem);
  const setAllChecked = useMutation(api.lists.setAllChecked);

  const [newListName, setNewListName] = useState("");
  const [itemDrafts, setItemDrafts] = useState<Record<string, string>>({});

  const draftFor = (id: string) => itemDrafts[id] ?? "";
  const setDraftFor = (id: string, value: string) =>
    setItemDrafts((prev) => ({ ...prev, [id]: value }));

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-2">
        <ListChecks className="w-5 h-5 text-primary" />
        <h1 className="page-title text-2xl font-semibold">Lists</h1>
      </div>

      <form
        onSubmit={async (event) => {
          event.preventDefault();
          const name = newListName.trim();
          if (!name) return;
          setNewListName("");
          await createList({ name });
        }}
        className="flex gap-2"
      >
        <Input
          value={newListName}
          onChange={(e) => setNewListName(e.target.value)}
          placeholder="New list…"
          aria-label="Name of the new list"
        />
        <Button type="submit" disabled={!newListName.trim()}>
          <Plus className="w-4 h-4 mr-1.5" />
          Add list
        </Button>
      </form>

      {lists === undefined && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {lists?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No lists yet. Shopping, packing, the steps of a job — anything you
            want to tick off.
          </CardContent>
        </Card>
      )}

      {lists?.map((list) => {
        const total = list.items.length;
        const done = list.doneCount;
        const allDone = total > 0 && done === total;

        return (
          <Card key={list._id}>
            <CardHeader className="pb-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base truncate">
                    {list.name}
                  </CardTitle>
                  {total > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {done} of {total} done
                    </p>
                  )}
                </div>

                {total > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={allDone ? "Untick everything" : "Tick everything"}
                    onClick={() =>
                      setAllChecked({ listId: list._id, checked: !allDone })
                    }
                  >
                    {allDone ? (
                      <RotateCcw className="w-4 h-4" />
                    ) : (
                      <ListChecks className="w-4 h-4" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete the list ${list.name}`}
                  className="text-destructive hover:text-destructive"
                  onClick={() => removeList({ id: list._id })}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {total > 0 && (
                <Progress value={(done / total) * 100} className="h-1 mt-2" />
              )}
            </CardHeader>

            <CardContent className="space-y-1">
              {list.items.map((item) => (
                <ListItemRow
                  key={item._id}
                  id={item._id}
                  text={item.text}
                  url={item.url}
                  notes={item.notes}
                  checked={item.checked}
                  onToggle={(checked) => updateItem({ id: item._id, checked })}
                  onRemove={() => removeItem({ id: item._id })}
                />
              ))}

              <form
                onSubmit={async (event) => {
                  event.preventDefault();
                  const text = draftFor(list._id).trim();
                  if (!text) return;
                  setDraftFor(list._id, "");
                  await addItem({ listId: list._id, text });
                }}
                className="flex gap-2 pt-2"
              >
                <Input
                  value={draftFor(list._id)}
                  onChange={(e) => setDraftFor(list._id, e.target.value)}
                  placeholder="Add an item…"
                  aria-label={`Add an item to ${list.name}`}
                  className="h-10"
                />
                <Button
                  type="submit"
                  size="icon"
                  variant="secondary"
                  className="h-10 w-10 shrink-0"
                  aria-label={`Add the item to ${list.name}`}
                  disabled={!draftFor(list._id).trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/** A single row. Sized for a thumb: the whole label is the hit target. */
function ListItemRow({
  id,
  text,
  url,
  notes,
  checked,
  onToggle,
  onRemove,
}: {
  id: Id<"listItems">;
  text: string;
  url?: string;
  notes?: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md px-2 -mx-2 min-h-[44px] hover:bg-muted/50 group">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onToggle(value === true)}
      />
      <label
        htmlFor={id}
        className={cn(
          "flex-1 min-w-0 text-sm py-2 cursor-pointer select-none",
          checked && "line-through text-muted-foreground",
        )}
      >
        {text}
        {notes && (
          <span className="block text-xs text-muted-foreground">{notes}</span>
        )}
      </label>

      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-muted-foreground hover:text-foreground p-2"
          aria-label={`Open the link for ${text}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}

      <Button
        variant="ghost"
        size="icon"
        aria-label={`Delete ${text}`}
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-destructive hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
